import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ExternalLink, CheckCircle2, AlertCircle, Unplug } from 'lucide-react';
import { useWallet } from '@/lib/wallet-context';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { cn } from '@/lib/utils';

const WALLET_OPTIONS = [
  {
    id: 'metamask',
    name: 'MetaMask',
    description: 'Ethereum, BSC, Polygon',
    chain: 'ethereum',
    color: '#F6851B',
    icon: (props) => (
      <svg viewBox="0 0 35 33" {...props}>
        <path d="M32.96 1l-13.14 9.72 2.45-5.73z" fill="#E2761B" stroke="#E2761B" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2.66 1l13.02 9.82L13.35 5l-10.7-4zm25.57 22.53l-3.5 5.34 7.49 2.06 2.14-7.28-6.13-.12zm-26.33.12l2.13 7.28 7.47-2.06-3.48-5.34-6.12.12z" fill="#E4761B" stroke="#E4761B" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M10.47 14.51l-2.08 3.14 7.4.34-.24-7.97-5.08 4.49zm14.68 0L20 10.02l-.17 7.97 7.4-.34-2.08-3.14zM11.73 28.87l4.44-2.16-3.83-2.99-.61 5.15zm8.1-2.16l4.44 2.16-.6-5.15-3.84 2.99z" fill="#E4761B" stroke="#E4761B" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    installUrl: 'https://metamask.io/download/',
  },
  {
    id: 'tronlink',
    name: 'TronLink',
    description: 'TRON (TRC-20)',
    chain: 'tron',
    color: '#FF0013',
    icon: (props) => (
      <svg viewBox="0 0 64 64" {...props}>
        <path d="M32 2L4 22l10 36h36l10-36L32 2z" fill="none" stroke="currentColor" strokeWidth="3"/>
        <path d="M32 14l-16 12 6 20h20l6-20L32 14z" fill="currentColor" opacity="0.3"/>
        <text x="32" y="40" textAnchor="middle" fontSize="16" fontWeight="bold" fill="currentColor">T</text>
      </svg>
    ),
    installUrl: 'https://www.tronlink.org/',
  },
  {
    id: 'walletconnect',
    name: 'WalletConnect',
    description: 'Trust Wallet, Rainbow, 700+',
    chain: 'ethereum',
    color: '#3B99FC',
    icon: (props) => (
      <svg viewBox="0 0 300 185" {...props}>
        <path d="M61.44 36.96c48.86-47.82 128.1-47.82 176.97 0l5.88 5.76c2.44 2.39 2.44 6.27 0 8.66l-20.13 19.7c-1.22 1.2-3.2 1.2-4.42 0l-8.09-7.92c-34.09-33.38-89.37-33.38-123.46 0l-8.67 8.49c-1.22 1.2-3.2 1.2-4.42 0L54.97 52.04c-2.44-2.39-2.44-6.27 0-8.66l6.47-6.42zM280.21 77.54l17.92 17.54c2.44 2.39 2.44 6.27 0 8.66L216.1 183.32c-2.44 2.39-6.4 2.39-8.84 0l-58.2-56.98c-.61-.6-1.6-.6-2.21 0l-58.2 56.98c-2.44 2.39-6.4 2.39-8.84 0L-2.23 103.74c-2.44-2.39-2.44-6.27 0-8.66l17.92-17.54c2.44-2.39 6.4-2.39 8.84 0l58.2 56.98c.61.6 1.6.6 2.21 0l58.2-56.98c2.44-2.39 6.4-2.39 8.84 0l58.2 56.98c.61.6 1.6.6 2.21 0l58.2-56.98c2.44-2.39 6.4-2.39 8.84 0z" fill="currentColor"/>
      </svg>
    ),
    installUrl: null,
  },
];

export function ConnectWalletModal({ open, onOpenChange, onWalletConnected, mode = 'link' }) {
  const { t } = useLanguage();
  const {
    connectedWallet,
    connecting,
    error,
    hasMetaMask,
    hasTronLink,
    connectMetaMask,
    connectTronLink,
    connectWalletConnect,
    disconnect,
    setError,
  } = useWallet();

  const [selectedWallet, setSelectedWallet] = useState(null);

  const isInstalled = (id) => {
    if (id === 'metamask') return hasMetaMask;
    if (id === 'tronlink') return hasTronLink;
    return true; // WalletConnect always available
  };

  const handleConnect = async (wallet) => {
    setSelectedWallet(wallet.id);
    setError('');
    try {
      let result;
      if (wallet.id === 'metamask') {
        result = await connectMetaMask();
      } else if (wallet.id === 'tronlink') {
        result = await connectTronLink();
      } else if (wallet.id === 'walletconnect') {
        result = await connectWalletConnect();
      }

      if (result && onWalletConnected) {
        onWalletConnected(result);
      }
    } catch (err) {
      // Error already set in context
    }
  };

  const title = mode === 'login' ? t('walletModal.loginTitle') : t('walletModal.linkTitle');
  const desc = mode === 'login' ? t('walletModal.loginDesc') : t('walletModal.linkDesc');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px] bg-[hsl(var(--card))] border-[hsl(var(--border))] p-0 gap-0">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle className="text-xl font-['Space_Grotesk']">{title}</DialogTitle>
          <DialogDescription className="text-[hsl(var(--muted-foreground))]">
            {desc}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-6 space-y-3">
          {error && (
            <Alert className="bg-[rgba(217,74,74,0.1)] border-[#D94A4A]/20">
              <AlertCircle className="w-4 h-4 text-[#D94A4A]" />
              <AlertDescription className="text-sm text-[#D94A4A]">{error}</AlertDescription>
            </Alert>
          )}

          {connectedWallet && (
            <Alert className="bg-[rgba(45,211,111,0.08)] border-[#2DD36F]/20">
              <CheckCircle2 className="w-4 h-4 text-[#2DD36F]" />
              <AlertDescription>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[#2DD36F]">
                      {t('walletModal.connected')}
                    </p>
                    <p className="text-xs font-mono text-[hsl(var(--muted-foreground))] mt-0.5">
                      {connectedWallet.address.slice(0, 8)}...{connectedWallet.address.slice(-6)}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={disconnect}
                    className="text-[hsl(var(--muted-foreground))] hover:text-[#D94A4A] h-7"
                  >
                    <Unplug className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {WALLET_OPTIONS.map((wallet) => {
            const installed = isInstalled(wallet.id);
            const isActive = selectedWallet === wallet.id && connecting;
            const Icon = wallet.icon;

            return (
              <button
                key={wallet.id}
                data-testid={`wallet-connect-${wallet.id}`}
                onClick={() => installed ? handleConnect(wallet) : window.open(wallet.installUrl, '_blank')}
                disabled={connecting}
                className={cn(
                  "w-full flex items-center gap-4 p-4 rounded-xl border transition-all duration-200",
                  "hover:bg-white/[0.03] active:scale-[0.99]",
                  isActive
                    ? "border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5"
                    : "border-[hsl(var(--border))] hover:border-[hsl(var(--border))]/80",
                  connecting && !isActive && "opacity-50 cursor-not-allowed"
                )}
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${wallet.color}15` }}
                >
                  <Icon className="w-6 h-6" style={{ color: wallet.color }} />
                </div>

                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{wallet.name}</span>
                    {installed ? (
                      <Badge className="text-[10px] h-4 bg-[rgba(45,211,111,0.1)] text-[#2DD36F] border-[#2DD36F]/20">
                        {t('walletModal.ready')}
                      </Badge>
                    ) : (
                      <Badge className="text-[10px] h-4 bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]">
                        {t('walletModal.install')}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{wallet.description}</p>
                </div>

                <div className="flex-shrink-0">
                  {isActive ? (
                    <Loader2 className="w-5 h-5 animate-spin text-[hsl(var(--primary))]" />
                  ) : !installed ? (
                    <ExternalLink className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-[hsl(var(--border))]" />
                  )}
                </div>
              </button>
            );
          })}

          <p className="text-xs text-center text-[hsl(var(--muted-foreground))] pt-2">
            {t('walletModal.terms')}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
